import React from 'react'
import { type ClassValue } from 'clsx'
import Link from 'next/link'
import { Category, Post } from '@/payload-types'

import { cn } from '@/utilities/ui'
import { ByAuthor } from '@/components/frontend/ByAuthor'
import { PublishedDateInfo } from '../PublishedDateInfo'
import { MediaProps, MediaWithFallback } from '../MediaWithFallback'
import { Locale } from '@/providers/Locale/types'
import { PartialPost } from '@/utilities/postToArticle'
import { slugToLink } from '@/utilities/slugToLink'
import { FlatTitle } from '../FlatTitle'
import { Breadcrumbs } from '../Breadcrumbs'

export interface Props {
    classes?: Partial<Record<'title' | 'article' | 'description' | 'image', ClassValue>>
    post: PartialPost & Pick<Partial<Post>, 'populatedAuthors'>
    variant?: 'default' | 'large'
    showDescription?: boolean
    showPublishTimeOnly?: boolean
    showBreadcrumbs?: boolean
    media: MediaProps
    useHero?: boolean
    locale: Locale
}

export const ArticleInList: React.FC<Props> = ({
    classes,
    media,
    post,
    variant = 'default',
    showDescription = false,
    showBreadcrumbs = false,
    locale,
    showPublishTimeOnly,
}) => {
    const postUrl = slugToLink(post.slug)
    const category = post?.categories?.slice(-1)[0]
    const postCategory: Category = category && typeof category !== 'string' ? category : { id: '', title: 'unknown', updatedAt: '', createdAt: '' }

    return (
        <article className={cn('flex flex-nowrap font-serif pt-1', { 'pt-7': variant === 'large' }, classes?.article)}>
            {media.media && (
                <Link
                    href={postUrl}
                    className={cn(
                        'block mr-4 w-20 h-20 lg:block lg:w-32 lg:h-auto flex-shrink-0',
                        {
                            'w-40 h-40 w-[80px] h-[80px] md:w-[120px] md:h-[120px] lg:w-[160px] lg:h-[160px]': variant === 'large',
                            'md:hidden': variant !== 'large',
                        },
                        classes?.image,
                    )}
                >
                    <MediaWithFallback
                        media={media.media}
                        options={{
                            sizes: '100vw',
                            width: 80,
                            height: 80,
                            ...media.options,
                            className: cn(
                                'object-cover max-w-none w-full',
                                {
                                    'w-[80px] h-[80px] md:w-[120px] md:h-[120px] lg:w-[160px] lg:h-[160px]': variant === 'large',
                                },
                                media.options?.className,
                            ),
                            src: media.options?.src,
                            alt: media.options?.alt || '',
                        }}
                    />
                </Link>
            )}
            <div className='flex-grow'>
                {showBreadcrumbs && postCategory.breadcrumbs && <Breadcrumbs crumbs={postCategory.breadcrumbs} />}
                <Link href={postUrl} className='hover:text-neutral-500'>
                    <h3 className={cn('text-sm font-semibold', { 'text-2xl sm:text-4xl font-bold mb-2 pb-2': variant === 'large' }, classes?.title)}>
                        <FlatTitle heading={post.title} />
                    </h3>
                </Link>
                {variant === 'large' && (
                    <div className=' hidden sm:flex flex-wrap font-sans gap-x-2 gap-y-1'>
                        <ByAuthor authors={post.populatedAuthors} postId={post.id} />
                        <div className='flex flex-row gap-2 overflow-hidden whitespace-nowrap' style={{ maxWidth: '100%' }}>
                            <PublishedDateInfo
                                showPublishTimeOnly={showPublishTimeOnly}
                                locale={locale}
                                publishedAt={post.publishedAt}
                                updatedAt={post.updatedAt}
                            />
                        </div>
                    </div>
                )}
                {variant !== 'large' && showDescription && <p className={cn(classes?.description)}>{post.meta?.description}</p>}
            </div>
        </article>
    )
}
